import { useState, useMemo } from 'react';
import { MetricCard, FeaturedMetricCard } from '@g17eco/ui-modern/components/ui/metric-card';
import { InsightsViewSelector } from './InsightsViewSelector';
import { ENTITY_VIEW_OPTIONS, type EntityView } from './insightsViewOptions';
import { AssetTypeFilter, type AssetFilterValue } from './AssetTypeFilter';
import { FacilitiesComparisonPanel } from '../FacilitiesComparisonPanel';
import { AssetComparisonPanel } from './AssetComparisonPanel';
import { useGetAssetsQuery, useGetEmissionsSummaryQuery } from '../../api/emissions';
import { useEmissionsFormatter } from '../../hooks/useEmissionsFormatter';
import { SCOPE_LUCIDE_ICONS, SCOPE_TAILWIND, SCOPE_COLORS } from '../../config/scopeBranding';
import { AssetType } from '../../types/emissions';
import { getDisabledAssetTypes } from './assetFilterUtils';
import { Leaf } from 'lucide-react';

interface ComparisonDashboardProps {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  includeChildren?: boolean;
}

export const ComparisonDashboard = ({ initiativeId, periodStart, periodEnd, includeChildren }: ComparisonDashboardProps) => {
  const [compareBy, setCompareBy] = useState<EntityView>('assets');
  const [selectedAssetType, setSelectedAssetType] = useState<AssetFilterValue>('all');
  const { data: assetsResponse } = useGetAssetsQuery({ initiativeId, limit: 100, includeChildren }, { skip: !initiativeId });
  const disabledTypes = useMemo(() => getDisabledAssetTypes(assetsResponse?.data ?? []), [assetsResponse?.data]);

  const { formatCompact } = useEmissionsFormatter();
  const { data: summary, isLoading: summaryLoading } = useGetEmissionsSummaryQuery(
    { initiativeId, periodStart, periodEnd, includeChildren },
    { skip: !initiativeId }
  );

  const scope1 = summary?.byScope?.scope1 ?? 0;
  const scope2 = summary?.byScope?.scope2 ?? 0;
  const scope3 = summary?.byScope?.scope3 ?? 0;
  const Scope1Icon = SCOPE_LUCIDE_ICONS.scope1;
  const Scope2Icon = SCOPE_LUCIDE_ICONS.scope2;
  const Scope3Icon = SCOPE_LUCIDE_ICONS.scope3;

  return (
    <div className='tw:space-y-6'>
      {/* Scope Summary Cards */}
      <div className='tw:grid tw:grid-cols-2 tw:lg:grid-cols-4 tw:gap-4'>
        <FeaturedMetricCard
          label='Total Emissions'
          value={formatCompact(summary?.totalCO2e ?? 0).formatted}
          unit={formatCompact(summary?.totalCO2e ?? 0).unit}
          icon={<Leaf className='tw:size-5 tw:text-white' />}
          isLoading={summaryLoading}
        />
        <MetricCard
          label='Scope 1'
          value={formatCompact(scope1).formatted}
          unit={formatCompact(scope1).unit}
          icon={<Scope1Icon className={`tw:size-5 ${SCOPE_TAILWIND.scope1.icon}`} />}
          iconBg={SCOPE_TAILWIND.scope1.iconBg}
          borderColor={SCOPE_TAILWIND.scope1.border}
          valueColor={SCOPE_COLORS.scope1}
          isLoading={summaryLoading}
        />
        <MetricCard
          label='Scope 2'
          value={formatCompact(scope2).formatted}
          unit={formatCompact(scope2).unit}
          icon={<Scope2Icon className={`tw:size-5 ${SCOPE_TAILWIND.scope2.icon}`} />}
          iconBg={SCOPE_TAILWIND.scope2.iconBg}
          borderColor={SCOPE_TAILWIND.scope2.border}
          valueColor={SCOPE_COLORS.scope2}
          isLoading={summaryLoading}
        />
        <MetricCard
          label='Scope 3'
          value={scope3 > 0 ? formatCompact(scope3).formatted : '—'}
          unit={scope3 > 0 ? formatCompact(scope3).unit : ''}
          icon={<Scope3Icon className={`tw:size-5 ${SCOPE_TAILWIND.scope3.icon}`} />}
          iconBg={SCOPE_TAILWIND.scope3.iconBg}
          borderColor={SCOPE_TAILWIND.scope3.border}
          valueColor={SCOPE_COLORS.scope3}
          isLoading={summaryLoading}
        />
      </div>

      <InsightsViewSelector
        label='Compare by'
        options={ENTITY_VIEW_OPTIONS}
        value={compareBy}
        onChange={setCompareBy}
      />

      {compareBy === 'assets' && (
        <AssetTypeFilter value={selectedAssetType} onChange={setSelectedAssetType} disabledTypes={disabledTypes} />
      )}

      {/* Panel content */}
      {compareBy === 'reporting-levels' && (
        <FacilitiesComparisonPanel
          initiativeId={initiativeId}
          periodStart={periodStart}
          periodEnd={periodEnd}
          includeChildren={includeChildren}
        />
      )}
      {compareBy === 'assets' && (
        <AssetComparisonPanel
          initiativeId={initiativeId}
          periodStart={periodStart}
          periodEnd={periodEnd}
          assetType={selectedAssetType === 'all' ? undefined : selectedAssetType as AssetType | 'unassigned'}
          includeChildren={includeChildren}
        />
      )}
    </div>
  );
};
